import { useState, useCallback } from 'react';
import { FormInstance } from 'antd';

interface UseModalOptions {
  form?: FormInstance;
  onOpen?: (id: string) => void;
  onClose?: () => void;
}

interface UseModalReturn {
  visible: boolean;
  itemId: string;
  showModal: (id: string) => void;
  hideModal: () => void;
  isEditing: boolean;
}

/**
 * Hook to manage modal visibility and item selection state
 * Optionally resets form fields when modal opens/closes
 *
 * @param options - Configuration options
 * @param options.form - Ant Design form instance to reset on modal actions
 * @param options.onOpen - Callback when modal opens
 * @param options.onClose - Callback when modal closes
 *
 * @example
 * const [form] = Form.useForm();
 * const { visible, itemId, showModal, hideModal, isEditing } = useModal({ form });
 *
 * // Open for new item
 * showModal('');
 *
 * // Open for editing existing item
 * showModal(existingItemId);
 */
export function useModal(options: UseModalOptions = {}): UseModalReturn {
  const { form, onOpen, onClose } = options;

  const [visible, setVisible] = useState<boolean>(false);
  const [itemId, setItemId] = useState<string>('');

  const showModal = useCallback((id: string) => {
    setVisible(true);
    setItemId(id);

    if (form && id === '') {
      form.resetFields();
    }

    onOpen?.(id);
  }, [form, onOpen]);

  const hideModal = useCallback(() => {
    setVisible(false);
    setItemId('');

    if (form) {
      form.resetFields();
    }

    onClose?.();
  }, [form, onClose]);

  return {
    visible,
    itemId,
    showModal,
    hideModal,
    isEditing: itemId !== '',
  };
}

export default useModal;
